// Shared SEO/head constants and helpers.
//
// Single source of truth for the site origin, the two absolute brand images, and the
// relative→absolute URL helper. Defining them here once keeps the assets consistent
// across the homepage, categories, stores, store, and category heads (D-12) — never
// `/bg.png` (a wide hero image with no branding).
//
// Two assets, because the two consumers want different shapes and one file cannot
// serve both: a square file fed to `summary_large_image`/Facebook's 1.91:1 frame gets
// centre-cropped and loses the top and bottom of the mark.
//
//   OG_IMAGE     1200x630 landscape — og:image / twitter:image (share previews)
//   SQUARE_LOGO  1254x1254 square   — Organization.logo JSON-LD, and the og:image
//                                     fallback on `summary` cards, which crop to 1:1
//
// Both are versioned (`-v2`): social scrapers and the ImageKit CDN cache by URL for a
// year, so a rebrand must ship a NEW filename rather than overwrite the old one.
// The pre-rebrand `brand/yetfarrag-logo.png` is deliberately left in place.

export const SITE_ORIGIN = "https://yetfarrag.com";

// Absolute landscape share image for og:image / twitter:image (D-12).
export const OG_IMAGE = "https://ik.imagekit.io/yetfarrag/brand/yetfarrag-og-v2.png";
export const OG_IMAGE_WIDTH = "1200";
export const OG_IMAGE_HEIGHT = "630";

// Absolute square brand logo for Organization.logo, and og:image on `summary` cards (D-12).
export const SQUARE_LOGO =
  "https://ik.imagekit.io/yetfarrag/brand/yetfarrag-logo-square-v2.png";

// Official social profiles, for Organization.sameAs on the home page. Footer.tsx renders the same
// two links from its own literal (kept there to avoid a Footer edit); the two must stay in sync.
export const SOCIAL_PROFILES = [
  "https://www.facebook.com/yetfarrag/",
  "https://www.instagram.com/yetfarrag/",
] as const;

export const toAbsoluteUrl = (value?: string | null): string | null => {
  if (!value) return null;

  try {
    return new URL(value, SITE_ORIGIN).toString();
  } catch {
    return null;
  }
};

// T-32-17: a branch's Google Maps URL is ALWAYS rebuilt from its `googlePlaceId` against this
// hard-coded prefix. The scraped `mapsUrl` stored on a branch is never used as a URL. Kept here so
// the rendered `href` (StoreBranchesCard) and the emitted `hasMap` (store +Head) cannot drift.
//
// `encodeURIComponent` is a no-op on well-formed place ids (`ChIJ…`, `[A-Za-z0-9_-]`) — it is
// defence in depth against a poisoned id breaking out of the query string.
export const googleMapsPlaceUrl = (placeId: string): string =>
  `https://www.google.com/maps/place/?q=place_id:${encodeURIComponent(placeId)}`;

// Category page <title>/<meta description> builders. C4: +title.ts, +description.ts,
// and +Head.tsx (twitter:*) must all emit the exact same strings — they call these
// instead of holding three copies of the formula. Count-bearing when the store total
// is known (SEO: "545 متجر ملابس نساء" beats a bare category name in the SERP).
// Review-count-bearing when the category has feed-eligible reviews: GSC (2026-10-05) showed the
// 127 category pages earning zero clicks in 90 days, and the review count is the one number that
// tells a review platform's category page apart from a marketplace listing. Numbers here stay in
// Western digits (SERP convention); on-page body copy uses toArabicNumerals.
type CategoryTitleInput = { title: { ar: string }; description?: string };

export const categoryPageTitle = (
  category: CategoryTitleInput,
  totalItems?: number,
  reviewCount?: number
): string => {
  if (reviewCount && reviewCount > 0 && totalItems && totalItems > 0) {
    return `${category.title.ar}: ${totalItems} متجر و${reviewCount} تقييم من المشترين | يتفرج`;
  }
  return totalItems && totalItems > 0
    ? `${category.title.ar}: ${totalItems} متجر بتقييمات حقيقية | يتفرج`
    : `${category.title.ar} - يتفرج`;
};

export const categoryPageDescription = (
  category: CategoryTitleInput,
  totalItems?: number,
  reviewCount?: number
): string => {
  if (reviewCount && reviewCount > 0 && totalItems && totalItems > 0) {
    const base = `تجارب المشترين مع متاجر ${category.title.ar} في مصر: ${reviewCount} تقييم على ${totalItems} متجر على يتفرج. اقرأ آخر التقييمات قبل ما تشتري.`;
    return category.description ? `${base} ${category.description}` : base;
  }
  if (totalItems && totalItems > 0) {
    const base = `تصفح ${totalItems} متجر في فئة ${category.title.ar} على يتفرج، بتقييمات حقيقية من المتسوقين قبل ما تشتري.`;
    return category.description ? `${base} ${category.description}` : base;
  }
  return category.description || `اكتشف متاجر فئة ${category.title.ar} على يتفرج`;
};

// Blog post <title>/<meta description> builders. Same C4 rule: the post's +title.ts,
// +description.ts and +Head.tsx (og:/twitter:*) all call these. `seoTitle` is the optional
// SERP-tuned override from frontmatter; the visible h1 always uses `title`.
type BlogTitleInput = { title: string; seoTitle?: string };

export const blogPostTitle = (fm: BlogTitleInput): string => `${fm.seoTitle || fm.title} | يتفرج`;

export const blogPostDescription = (fm: { description: string }): string => fm.description;

// Blacklist page <title>/<meta description> builders. Same C4 rule as the category pair above:
// +title.ts, +description.ts and +Head.tsx (twitter:*) all call these rather than holding three
// copies of the formula, so the three files cannot drift.
//
// COUNT-BEARING. «بلاك ليست التقييمات الوهمية على فيسبوك · ٣٣٧٨ صفحة» beats a bare title in the
// SERP, and the primary keyword «بلاك ليست» currently returns only Facebook groups and zero
// ranking websites.
//
// NO EM DASH. Project rule for public Arabic copy — it reads as machine-written. The separator
// here is a middle dot.
type BlacklistSummaryInput = { pages: number; reviews: number };

export const blacklistPageTitle = (summary?: BlacklistSummaryInput | null): string =>
  summary && summary.pages > 0
    ? `بلاك ليست التقييمات الوهمية على فيسبوك · ${summary.pages} صفحة | يتفرج`
    : `بلاك ليست التقييمات الوهمية على فيسبوك | يتفرج`;

export const blacklistPageDescription = (summary?: BlacklistSummaryInput | null): string =>
  summary && summary.pages > 0
    ? `دوّر على أي صفحة فيسبوك وشوف لو فيها تقييمات وهمية. رصدنا ${summary.reviews} تقييم مزيف على ${summary.pages} صفحة، من حسابات بتكتب مراجعات مزيفة بالجملة، وكل تقييم تقدر تقراه وتتأكد منه بنفسك.`
    : `دوّر على أي صفحة فيسبوك وشوف لو فيها تقييمات وهمية قبل ما تشتري، بمراجعات مرصودة وأدلة تقدر تتأكد منها بنفسك.`;

// ─── openingHoursSpecification from Google weekday_text ───
//
// Google Places is called with language:'ar' (googlePlacesService.ts), so
// `openingHours` / branch `workingHours` hold DISPLAY strings like
// "السبت: 10:00 ص – 10:00 م" or "الجمعة: ١:٠٠–١٠:٠٠ م" — schema.org can't read
// those. This parses them into OpeningHoursSpecification nodes. Rules:
// unparseable line → skipped; nothing parseable → return null and the caller
// must OMIT the property (raw display strings must never ship in JSON-LD).
// A lone trailing ص/م applies to both ends of the range ("١:٠٠–١٠:٠٠ م" = 1PM–10PM),
// matching how Google elides the shared meridiem. Overnight ranges are emitted
// as-is (closes < opens means "next day" per schema.org).

type OpeningHoursSpec = {
  "@type": "OpeningHoursSpecification";
  dayOfWeek: string;
  opens: string;
  closes: string;
};

const WEEKDAY_MAP: Record<string, string> = {
  "الأحد": "Sunday",
  "الاثنين": "Monday",
  "الإثنين": "Monday",
  "الثلاثاء": "Tuesday",
  "الأربعاء": "Wednesday",
  "الخميس": "Thursday",
  "الجمعة": "Friday",
  "السبت": "Saturday",
  Sunday: "Sunday",
  Monday: "Monday",
  Tuesday: "Tuesday",
  Wednesday: "Wednesday",
  Thursday: "Thursday",
  Friday: "Friday",
  Saturday: "Saturday",
};

// Arabic-Indic (٠-٩) and Eastern Arabic-Indic (۰-۹) digits → ASCII.
const normalizeDigits = (value: string): string =>
  value
    .replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x0660))
    .replace(/[۰-۹]/g, (d) => String(d.charCodeAt(0) - 0x06f0));

// "10:00 ص" / "1:00" / "10 م" → { h, m, meridiem? }. Returns null on garbage.
const parseClockTime = (
  raw: string,
): { h: number; m: number; meridiem: "am" | "pm" | null } | null => {
  const text = normalizeDigits(raw).trim();
  const match = text.match(/^(\d{1,2})(?::(\d{2}))?\s*(ص|م|AM|PM|am|pm)?$/);
  if (!match) return null;
  const h = parseInt(match[1], 10);
  const m = match[2] ? parseInt(match[2], 10) : 0;
  if (h > 23 || m > 59) return null;
  const meridiem =
    match[3] === "ص" || match[3]?.toLowerCase() === "am"
      ? "am"
      : match[3] === "م" || match[3]?.toLowerCase() === "pm"
        ? "pm"
        : null;
  return { h, m, meridiem };
};

const to24h = (h: number, meridiem: "am" | "pm" | null): number => {
  if (meridiem === "am") return h === 12 ? 0 : h;
  if (meridiem === "pm") return h === 12 ? 12 : h + 12;
  return h;
};

const formatHHMM = (h: number, m: number): string =>
  `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;

export const parseGoogleWeekdayText = (
  lines: string[] | undefined | null,
): OpeningHoursSpec[] | null => {
  if (!Array.isArray(lines) || lines.length === 0) return null;

  const specs: OpeningHoursSpec[] = [];
  for (const line of lines) {
    if (typeof line !== "string") continue;
    const colonIndex = line.indexOf(":");
    if (colonIndex < 1) continue;
    const day = WEEKDAY_MAP[stripBidiControls(line.slice(0, colonIndex))];
    const rest = line.slice(colonIndex + 1).trim();
    if (!day || !rest) continue;

    if (/مغلق|closed/i.test(rest)) continue; // closed day → no spec

    if (/24|٢٤/.test(normalizeDigits(rest)) && /ساعة|hour/i.test(rest)) {
      specs.push({
        "@type": "OpeningHoursSpecification",
        dayOfWeek: `https://schema.org/${day}`,
        opens: "00:00",
        closes: "23:59",
      });
      continue;
    }

    // Multiple ranges ("9:00 ص – 1:00 م، 4:00 م – 10:00 م") → one spec each.
    for (const range of rest.split(/[،,]/)) {
      const parts = range.split(/[–—-]/).map((part) => part.trim());
      if (parts.length !== 2) continue;
      const open = parseClockTime(parts[0]);
      const close = parseClockTime(parts[1]);
      if (!open || !close) continue;
      // Shared elided meridiem: "1:00–10:00 م" means both are PM.
      const openMeridiem = open.meridiem ?? close.meridiem;
      specs.push({
        "@type": "OpeningHoursSpecification",
        dayOfWeek: `https://schema.org/${day}`,
        opens: formatHHMM(to24h(open.h, openMeridiem), open.m),
        closes: formatHHMM(to24h(close.h, close.meridiem), close.m),
      });
    }
  }

  return specs.length > 0 ? specs : null;
};

// Scraped Google values carry bidi control characters — `governorate` is stored as "القاهرة‬"
// (8 codepoints, not 7). Strip them before rendering or de-duplicating, or a later clean "القاهرة"
// dedupes as a second distinct governorate and an invisible control char leaks into the page.
export const stripBidiControls = (value: string): string =>
  value.replace(/[‎‏‪-‮⁦-⁩]/g, "").trim();

// Egyptian formatted addresses spell the governorate as "محافظة <name>", the same convention
// storeBranchService.ts's GOVERNORATE_RE relies on. This pulls addressLocality out of a Google
// formatted_address string WITHOUT touching streetAddress — same source, not a blended one, so
// it doesn't reintroduce the cross-source contradiction the single-source PostalAddress rule
// in +Head.tsx guards against. A miss returns null on purpose: guessing is worse than blank.
export const extractGovernorate = (formattedAddress: string): string | null =>
  /محافظة\s+(\S+)/.exec(formattedAddress)?.[1] ?? null;

// Shared schema.org Review node mapper. Used by the store page (5 newest, filtered for text) and
// the reviews archive (the page's visible reviews), so both emit byte-identical Review nodes and
// the author/date fallback rules (external author vs account name, externalDate vs createdAt)
// cannot drift between the two heads.
export type ReviewJsonLdInput = {
  text?: string | null;
  overallRating: number;
  createdAt?: string;
  isExternal?: boolean;
  externalAuthor?: string;
  externalDate?: string;
  user?: { full_name?: string } | null;
};

export const reviewJsonLd = (r: ReviewJsonLdInput) => ({
  "@type": "Review",
  author: {
    "@type": "Person",
    name: r.isExternal ? r.externalAuthor : r.user?.full_name || "مستخدم يتفرج",
  },
  datePublished: r.isExternal ? r.externalDate : r.createdAt,
  reviewBody: r.text,
  reviewRating: {
    "@type": "Rating",
    ratingValue: String(r.overallRating),
    bestRating: "5",
    worstRating: "1",
  },
});
